import { Routes } from '@angular/router';
import { LoginRedirectComponent } from '@app/auth/login-redirect.component';
import { SignupRedirectComponent } from '@app/auth/signup-redirect.component';
import { AuthGuard } from '@auth0/auth0-angular';
import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';
import { HasStoresGuard } from './_shared/guards/has-store-guard.guard';
import { AuthComponent } from './auth/auth.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { DashboardEffects } from './dashboard/store/dashboard.effects';
import * as fromDashboard from './dashboard/store/dashboard.reducer';
import { MainLayoutComponent } from './main-layout/main-layout.component';
import { MainLayoutGuard } from './main-layout/main-layout.guard';

export const routes: Routes = [
  {
    path: 'auth',
    component: AuthComponent,
    canActivate: [],
  },
  {
    path: 'signup',
    component: SignupRedirectComponent,
    canActivate: [],
  },
  {
    path: 'login',
    component: LoginRedirectComponent,
    canActivate: [],
  },
  {
    path: 'login-redirect',
    component: LoginRedirectComponent,
    canActivate: [],
  },
  {
    matcher: (url) => {
      const firstUrlSegment = url[0];
      const firstUrlSegmentPath = firstUrlSegment?.path ?? '';

      // Check if the path matches any onboarding routes (they're nested under children now)
      const onboardingPaths = ['oauth', 'stores', 'onboarding'];
      if (onboardingPaths.includes(firstUrlSegmentPath)) {
        return {
          consumed: [],
        };
      }

      return null;
    },
    loadChildren: () => import('./onboarding/onboarding-feature.routes').then((m) => m.ONBOARDING_ROUTES),
    canActivate: [AuthGuard, MainLayoutGuard],
    canActivateChild: [AuthGuard],
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [AuthGuard, MainLayoutGuard],
    canActivateChild: [AuthGuard],
    children: [
      {
        path: '',
        component: DashboardComponent,
        pathMatch: 'full',
        providers: [
          provideState(fromDashboard.dashboardFeatureKey, fromDashboard.reducer),
          provideEffects(DashboardEffects),
        ],
      },
      {
        path: 'settings',
        loadChildren: () => import('./settings/settings.routes').then((m) => m.SETTINGS_ROUTES),
      },
      {
        path: 'integrations',
        redirectTo: 'settings/store',
      },
      {
        path: 'integrations/:storeId/subscriptions', // This is important to maintain as we're not able to update the redirect URL in shopify
        redirectTo: 'settings/store/:storeId',
      },
      {
        path: 'orders',
        canActivate: [HasStoresGuard],
        loadChildren: () => import('./order/order.routes').then((m) => m.orderRoutes),
      },
      {
        path: 'payouts',
        canActivate: [HasStoresGuard],
        loadChildren: () => import('./payout/payout.routes').then((m) => m.payoutRoutes),
      },
      {
        path: 'account',
        redirectTo: 'settings/company',
        pathMatch: 'full',
      },
      {
        path: 'account/:path',
        redirectTo: 'settings/company',
        pathMatch: 'prefix',
      },
      {
        path: 'payments',
        canActivate: [HasStoresGuard],
        loadChildren: () =>
          import('./incoming-transactions/incoming-transactions.routes').then((m) => m.INCOMING_TRANSACTIONS_ROUTES),
      },
      {
        path: 'refunds',
        canActivate: [HasStoresGuard],
        loadChildren: () => import('./refunds/refunds.routes').then((m) => m.REFUNDS_ROUTES),
      },
      {
        path: 'refund-transactions',
        canActivate: [HasStoresGuard],
        loadChildren: () =>
          import('./outgoing-transactions/outgoing-transactions.routes').then((m) => m.OUTGOING_TRANSACTIONS_ROUTES),
      },
      {
        path: 'invoice-settings',
        canActivate: [HasStoresGuard],
        loadChildren: () =>
          import('./settings/store-settings/invoice-settings/invoice-settings.routes').then(
            (m) => m.INVOICE_SETTINGS_ROUTES,
          ),
      },
      {
        path: 'corrections',
        canActivate: [HasStoresGuard],
        loadChildren: () => import('./corrections/corrections.routes').then((m) => m.CORRECTIONS_ROUTES),
      },
      {
        path: 'bundles',
        canActivate: [HasStoresGuard],
        loadChildren: () => import('./bundle/bundle.routes').then((m) => m.BUNDLE_ROUTES),
      },
      {
        path: 'fulfillments',
        canActivate: [HasStoresGuard],
        loadChildren: () => import('./fulfillments/fulfillments.routes').then((m) => m.fulfillmentsRoutes),
      },
      {
        path: 'returns',
        canActivate: [HasStoresGuard],
        loadChildren: () => import('./returns/returns.routes').then((m) => m.RETURNS_ROUTES),
      },
      {
        path: 'tools',
        canActivate: [HasStoresGuard],
        loadChildren: () => import('./tools/tools.routes').then((m) => m.TOOLBOX_ROUTES),
      },
      {
        path: 'bank',
        loadChildren: () => import('./bank/bank.routes').then((m) => m.BANK_ROUTES),
        canActivate: [HasStoresGuard],
      },
      {
        path: 'accounting-entries',
        canActivate: [HasStoresGuard],
        loadChildren: () =>
          import('./accounting-entries/accounting-entries.routes').then((m) => m.ACCOUNTING_ENTRIES_ROUTES),
      },
      {
        path: 'mailed-documents',
        canActivate: [HasStoresGuard],
        loadChildren: () => import('./mailed-documents/mailed-documents.routes').then((m) => m.MAILED_DOCUMENTS_ROUTES),
      },
      {
        path: '',
        canActivate: [HasStoresGuard],
        loadChildren: () => import('./customers/customers.routes').then((m) => m.CUSTOMERS_ROUTES),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
